import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { ShieldQuestion, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"
import { TemplateField } from "./template-field"

/**
 * Approval node (#29 HITL): pauses the run until a human approves or denies via
 * the resume API. On approve the input passes through; on deny it emits a denial
 * marker (pair with a conditional to branch). Pausing needs a durable run
 * (scheduled / async / resumable) — interactive test runs without a checkpoint
 * pass straight through.
 */
export function ApprovalNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const promptMessage = (data.promptMessage as string) || ""
  const hasOutput = isCompleted && !!output

  // #15 approval lifecycle config (all optional, default off = today's behavior).
  const recipients = (data.recipients as string[] | undefined) ?? []
  const escalateTo = (data.escalate_to as string[] | undefined) ?? []
  const timeoutMinutes = (data.timeout_minutes as number | null | undefined) ?? null
  const escalateAfterHours = (data.escalate_after_hours as number | null | undefined) ?? null

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  // Comma-separated email list <-> string[]. Empty string clears to [].
  const parseEmailList = (value: string): string[] =>
    value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean)

  // Optional positive number input <-> number|null. Empty/invalid clears to null.
  const parseOptionalNumber = (value: string): number | null => {
    const trimmed = value.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }

  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label className="text-xs">Approval prompt</Label>
        <TemplateField
          multiline
          className={cn("pr-8", isModal ? "min-h-[260px]" : "min-h-16")}
          placeholder="What should the approver confirm before the flow continues?"
          value={promptMessage}
          onChange={(value) => updateNodeData(id, { promptMessage: value })}
        />
        <p className="text-muted-foreground text-[10px]">
          Pauses a durable run until approved/denied via the run's resume action.
        </p>
      </div>

      <div className="grid gap-1.5">
        <Label className="text-xs">Notify recipients</Label>
        <Input
          className="nodrag h-8 text-xs"
          placeholder="approver@example.com, manager@example.com"
          value={recipients.join(", ")}
          onChange={(event) =>
            updateNodeData(id, { recipients: parseEmailList(event.target.value) })
          }
        />
        <p className="text-muted-foreground text-[10px]">
          Comma-separated emails to notify when the flow pauses. Leave empty to skip notifications.
        </p>
      </div>

      <div className="grid gap-1.5">
        <Label className="text-xs">Timeout (minutes)</Label>
        <Input
          className="nodrag h-8 text-xs"
          min={1}
          placeholder="No timeout"
          type="number"
          value={timeoutMinutes ?? ""}
          onChange={(event) =>
            updateNodeData(id, {
              timeout_minutes: parseOptionalNumber(event.target.value),
            })
          }
        />
        <p className="text-muted-foreground text-[10px]">
          Leave timeout empty to wait indefinitely. On timeout the flow is auto-denied.
        </p>
      </div>

      <div className="grid gap-1.5">
        <Label className="text-xs">Escalate after (hours)</Label>
        <Input
          className="nodrag h-8 text-xs"
          min={0.5}
          placeholder="No escalation"
          step={0.5}
          type="number"
          value={escalateAfterHours ?? ""}
          onChange={(event) =>
            updateNodeData(id, {
              escalate_after_hours: parseOptionalNumber(event.target.value),
            })
          }
        />
        <p className="text-muted-foreground text-[10px]">
          Send a one-time reminder to the escalation list once the run has waited this long.
        </p>
      </div>

      <div className="grid gap-1.5">
        <Label className="text-xs">Escalate to</Label>
        <Input
          className="nodrag h-8 text-xs"
          placeholder="lead@example.com, oncall@example.com"
          value={escalateTo.join(", ")}
          onChange={(event) =>
            updateNodeData(id, { escalate_to: parseEmailList(event.target.value) })
          }
        />
        <p className="text-muted-foreground text-[10px]">
          Comma-separated emails for the escalation reminder. Leave empty to skip.
        </p>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[240px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[3])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <ShieldQuestion className="size-4" />
            Approval
            <NodeEditDialog
              description="Edit this approval node in a larger workspace."
              title="Approval settings"
            >
              {renderEditor(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
